import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/ja';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-tw';
import dayjsRelativeTime from 'dayjs/plugin/relativeTime';
import dayjsTimezone from 'dayjs/plugin/timezone';
import dayjsUtc from 'dayjs/plugin/utc';

dayjs.extend(dayjsUtc);
dayjs.extend(dayjsTimezone);
dayjs.extend(dayjsRelativeTime);

/**
 * @returns 오늘 날짜
 */
export const utilDateToday = (): Date => {
  return dayjs().startOf('day').toDate();
};

/**
 * @returns 이번 달 첫 날
 */
export const utilDateThisMonth = (): Date => {
  return dayjs().startOf('month').toDate();
};

/**
 * @param date 날짜
 * @returns 해당 날짜의 월 첫 날
 */
export const utilDateGetMonth = (date: string | Date): Date => {
  return dayjs(date).startOf('month').toDate();
};

/**
 * @param month 월 수
 * @returns 최대 월 수
 */
export const utilDateMaxMonth = (month: number): Date => {
  return dayjs(utilDateToday()).add(month, 'month').endOf('month').toDate();
};

/**
 * @param date 날짜
 * @returns 이전 달 첫 날
 */
export const utilDatePrevMonth = (date: Date): Date => {
  return dayjs(date).subtract(1, 'month').startOf('month').toDate();
};

/**
 * @param date 날짜
 * @returns 다음 달 첫 날
 */
export const utilDateNextMonth = (date: Date): Date => {
  return dayjs(date).add(1, 'month').startOf('month').toDate();
};

/**
 * @param date1 날짜1
 * @param date2 날짜2
 * @returns 두 날짜의 월 차이
 */
export const utilDateDiffMonth = (date1: Date, date2: Date): number => {
  return dayjs(date1).diff(dayjs(date2), 'month');
};

/**
 * @param date 날짜
 * @returns 해당 달의 일 수
 */
export const utilDateDaysInMonth = (date: Date): number => {
  return dayjs(date).daysInMonth();
};

/**
 * @param date 날짜
 * @returns 해당 달의 첫 날
 */
export const utilDateFirstDayOfMonth = (date: Date): number => {
  return dayjs(date).startOf('month').day();
};

/**
 * @param currentMonth 현재 달
 * @param day 일
 * @returns 해당 날짜가 지났는지 여부
 */
export const utilDateIsPast = (currentMonth: Date, day: number): boolean => {
  return dayjs(currentMonth).date(day).isBefore(utilDateToday(), 'day');
};

/**
 * @param date1 날짜1
 * @param date2 날짜2
 * @returns 두 날짜가 같은 날인지 여부
 */
export const utilDateIsSameDay = (date: Date | null, currentMonth: Date, day: number): boolean => {
  return date ? dayjs(date).isSame(dayjs(currentMonth).date(day), 'day') : false;
};

/**
 * @param date 날짜
 * @param format 형식
 * @returns example: '2025-07-22' | '07-22' | '07.22 (월)' | '25.07.22 (월)' | '12:00'
 */
export const utilDateFormat = (
  date: string | Date,
  format: 'YYYY-MM-DD' | 'MM.DD' | 'MM.DD (dd)' | 'YY.MM.DD (dd)' | 'HH:mm',
): string => {
  return dayjs(date).format(format);
};

interface IDecreaseParams {
  date: string | Date;
  type: 'day' | 'month' | 'year';
  value: number;
}
export const utilDateDecrease = ({ date, type, value }: IDecreaseParams): Date => {
  return dayjs(date).subtract(value, type).toDate();
};

/**
 * @param date1 날짜1
 * @param date2 날짜2
 * @returns 두 날짜의 일수 차이 (date1 - date2)
 */
export const utilDateDiffDays = (date1: Date | string, date2: Date | string): number => {
  return dayjs(date1).diff(dayjs(date2), 'day');
};
